import axios from "axios";
import * as React from "react";
import { Confirm, Form, Popup, Progress } from "semantic-ui-react";
import { toast } from "@teamprevent/common/toasts";
import { OhCaseDocumentFile } from "@teamprevent/common/questionnaires/model";

interface OwnProps {
    apiUrl: string;
    onUpload?: (file: File) => Promise<string | OhCaseDocumentFile>;
    label?: string;
    popupText?: string;
    fileName?: string;
    onFileChange?: (value: string, fileName: string, ohCaseDocumentId?: string) => void;
    acceptExtensions?: string[];
    showFileName?: boolean;
    warningText?: string;
}

interface State {
    fileName: string;
    loading: boolean;
    progress: number;
    error: boolean;
    errorMessage: string;
    uploaded: boolean;
    fileId: string;
    showConfirmModal: boolean;
}

export class FileUpload extends React.Component<OwnProps, State> {
    private fileInput: HTMLInputElement;

    constructor(props: OwnProps) {
        super(props);
        this.state = {
            fileName: props.fileName,
            loading: false,
            progress: 0,
            error: false,
            errorMessage: "",
            uploaded: false,
            fileId: "",
            showConfirmModal: false
        };
        this.handleFileChange = this.handleFileChange.bind(this);
        this.fileInputClick = this.fileInputClick.bind(this);
        this.showConfirmModal = this.showConfirmModal.bind(this);
    }

    public render() {
        const { error, loading, fileName, progress, uploaded } = this.state;
        const button =
            (
                <Form.Button
                    label={this.props.showFileName === false ? "" : fileName || this.props.fileName}
                    error={error}
                    loading={loading}
                    type="button"
                    content={this.props.label || "Select"}
                    onClick={this.props.warningText ? this.showConfirmModal : this.fileInputClick}
                    value={fileName}
                />);

        return (
            <div>
                <input
                    type="file"
                    accept={this.props.acceptExtensions && this.props.acceptExtensions.join(",")}
                    onChange={this.handleFileChange}
                    ref={input => this.fileInput = input}
                    hidden
                />
                {this.props.popupText ?
                    <Popup
                        content={this.props.popupText}
                        trigger={button}
                    /> :
                    button
                }
                {loading &&
                    <Progress
                        percent={progress}
                        size="tiny"
                        active={progress < 100 && (!uploaded || !error)}
                        success={uploaded}
                        error={error}
                    />}
                <Confirm
                    open={this.state.showConfirmModal}
                    confirmButton="Confirm"
                    cancelButton="Cancel"
                    content={this.props.warningText}
                    onConfirm={this.fileInputClick}
                    onCancel={() => this.setState({ showConfirmModal: false })}
                />
            </div>
        );
    }

    private fileInputClick() {
        this.setState({ showConfirmModal: false });
        this.fileInput.click();
    }

    private showConfirmModal() {
        this.setState({
            showConfirmModal: true
        });
    }

    private uploadFile = async (file: File): Promise<string | OhCaseDocumentFile> => {
        if (this.props.onUpload) {
            return this.props.onUpload(file);
        } else {
            const data = new FormData();
            data.append("file", file);

            const response = await axios.post(this.props.apiUrl, data, {
                onUploadProgress: progress => {
                    this.setState({
                        progress: (progress.loaded / progress.total) * 90
                    });
                }
            });
            return response.data;
        }
    };

    private async handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
        const file = event.target.files[0];
        const validExtensions = this.props.acceptExtensions;
        if (validExtensions && !validExtensions.some(ext => file.name.toUpperCase().endsWith(ext.toUpperCase()))) {
            toast.error(`File extension must be one of ${this.props.acceptExtensions.join(", ")}`);
            return;
        }

        this.setState({
            fileName: file.name,
            loading: true,
            progress: 0
        });

        try {
            const result = await this.uploadFile(file);

            const fileId: string = typeof result === "string" ? result : result.blobId;
            const ohCaseDocumentId: string |undefined = typeof result === "string" ? undefined: result.ohCaseDocumentId;

            this.setState({
                fileId,
                uploaded: true,
                progress: 100
            });

            if (this.props.onFileChange) {
                this.props.onFileChange(fileId, file.name, ohCaseDocumentId );
            }
        } catch (error) {
            this.setState({
                error: true,
                errorMessage: error.response && error.response.data && error.response.data.message
            });
            toast.error("The file could not be uploaded");
        } finally {
            this.setState({
                loading: false
            });
        }
    }
}
